import Link from "next/link";
import { Check, ArrowRight, Loader2 } from "@/components/icons";
import { cn } from "@/lib/utils";
import { Button, buttonVariants } from "@/components/ui/button";
import type { PricingTier, BillingInterval } from "../data";
import { getAnnualPrice } from "../data";
import { ENTERPRISE_CONTACT_HREF } from "@shared/subscriptions/enterprise";

interface CTAProps {
  tier: PricingTier;
  isEnterprise: boolean;
  isFree: boolean;
  isSetupMode: boolean;
  isPaidTier: boolean;
  showCheckoutButton: boolean;
  isLoading: boolean;
  disabled?: boolean;
  onClick: () => void;
}

type CtaVariant = "primary" | "outline";

function freeTierCta(
  {
    tier,
    isSetupMode,
    disabled,
    onClick,
  }: Pick<CTAProps, "tier" | "isSetupMode" | "disabled" | "onClick">,
  variant: CtaVariant,
  ctaClassName: string,
) {
  if (isSetupMode) {
    return (
      <Button
        type="button"
        variant={variant}
        onClick={onClick}
        disabled={disabled}
        className={ctaClassName}
      >
        Get Started Free
        <ArrowRight className="h-4 w-4" />
      </Button>
    );
  }
  return (
    <a
      href={tier.cta.href}
      className={buttonVariants({ variant, className: ctaClassName })}
    >
      {tier.cta.label}
      <ArrowRight className="h-4 w-4" />
    </a>
  );
}

function paidTierCta(
  {
    tier,
    isSetupMode,
    isLoading,
    showCheckoutButton,
    disabled,
    onClick,
  }: Omit<CTAProps, "isEnterprise" | "isFree" | "isPaidTier">,
  variant: CtaVariant,
  ctaClassName: string,
) {
  return (
    <Button
      type="button"
      variant={variant}
      onClick={onClick}
      disabled={isLoading || disabled}
      className={ctaClassName}
    >
      {isLoading ? (
        <>
          <Loader2 className="h-4 w-4 animate-spin" />
          Processing...
        </>
      ) : (
        <>
          {isSetupMode || showCheckoutButton
            ? "Start Free Trial"
            : tier.cta.label}
          <ArrowRight className="h-4 w-4" />
        </>
      )}
    </Button>
  );
}

export function PricingCTA({
  tier,
  isEnterprise,
  isFree,
  isSetupMode,
  isLoading,
  showCheckoutButton,
  disabled,
  onClick,
}: CTAProps) {
  const variant = tier.isPopular ? "primary" : "outline";
  const ctaClassName = "rounded-none px-4 py-3 sm:py-2.5 min-h-11";

  if (isEnterprise) {
    return (
      <Link
        href={tier.cta.href}
        className={buttonVariants({ variant, className: ctaClassName })}
      >
        {tier.cta.label}
        <ArrowRight className="h-4 w-4" />
      </Link>
    );
  }

  if (isFree) {
    return freeTierCta(
      { tier, isSetupMode, disabled, onClick },
      variant,
      ctaClassName,
    );
  }

  return paidTierCta(
    { tier, isSetupMode, isLoading, showCheckoutButton, disabled, onClick },
    variant,
    ctaClassName,
  );
}

export function PricingCardContent({
  tier,
  billingInterval,
  isSelectionMode,
  renderCTA,
  onSelectEnterprise,
}: {
  tier: PricingTier;
  billingInterval: BillingInterval;
  isSelectionMode: boolean;
  renderCTA: () => React.ReactNode;
  onSelectEnterprise?: (event: React.MouseEvent) => void;
}) {
  const isEnterprise = tier.priceMonthly === null;

  return (
    <>
      <TierHeader badge={tier.badge} name={tier.name} />

      <div className="flex flex-1 flex-col p-4">
        <TierPrice tier={tier} billingInterval={billingInterval} />

        <p className="mb-4 text-sm text-muted-foreground">{tier.description}</p>

        {tier.highlight && !isEnterprise && (
          <TierHighlight highlight={tier.highlight} />
        )}

        <TierFeatureList
          features={tier.features}
          isSelectionMode={isSelectionMode}
        />

        {!isSelectionMode && renderCTA()}

        {isSelectionMode && isEnterprise && (
          <EnterpriseContactLink onSelectEnterprise={onSelectEnterprise} />
        )}
      </div>
    </>
  );
}

function TierPrice({
  tier,
  billingInterval,
}: {
  tier: PricingTier;
  billingInterval: BillingInterval;
}) {
  const price =
    billingInterval === "annual"
      ? getAnnualPrice(tier.priceMonthly)
      : tier.priceMonthly;
  const isEnterprise = tier.priceMonthly === null;
  const isFree = tier.priceMonthly === 0;
  return (
    <PriceDisplay
      isEnterprise={isEnterprise}
      isFree={isFree}
      price={price}
      billingInterval={billingInterval}
      priceMonthly={tier.priceMonthly}
      highlight={tier.highlight}
    />
  );
}

function TierHeader({ badge, name }: { badge?: string; name: string }) {
  return (
    <>
      {badge && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2 z-10">
          <span className="bg-action-fill px-3 py-1 text-xs font-medium text-action-fill-foreground whitespace-nowrap">
            {badge}
          </span>
        </div>
      )}

      <div className="border-b border-border bg-muted px-4 py-3">
        <h3 className="text-xs font-medium text-muted-foreground">{name}</h3>
      </div>
    </>
  );
}

function TierHighlight({ highlight }: { highlight: string }) {
  return (
    <p
      className={cn(
        "mb-4 text-xs",
        highlight.includes("*") || highlight.toLowerCase().includes("rebate")
          ? "rounded border border-warning/20 bg-warning-bg p-2 text-warning-strong"
          : "italic text-muted-foreground",
      )}
    >
      {highlight}
    </p>
  );
}

function TierFeatureList({
  features,
  isSelectionMode,
}: {
  features: PricingTier["features"];
  isSelectionMode: boolean;
}) {
  return (
    <ul className={cn("flex-1 space-y-2", !isSelectionMode && "mb-6")}>
      {features.map((feature, index) => (
        <li
          key={index}
          className="flex items-start gap-2 text-xs text-muted-foreground"
        >
          <Check className="h-3 w-3 shrink-0 text-success-strong mt-0.5" />
          {feature}
        </li>
      ))}
    </ul>
  );
}

function EnterpriseContactLink({
  onSelectEnterprise,
}: {
  onSelectEnterprise?: (event: React.MouseEvent) => void;
}) {
  return (
    <Link
      href={ENTERPRISE_CONTACT_HREF}
      className={buttonVariants({
        variant: "outline",
        className: "mt-4 rounded-none px-4 py-3 sm:py-2.5 min-h-11",
      })}
      onClick={onSelectEnterprise}
    >
      Contact Sales
      <ArrowRight className="h-4 w-4" />
    </Link>
  );
}

function PriceDisplay({
  isEnterprise,
  isFree,
  price,
  billingInterval,
  priceMonthly,
  highlight,
}: {
  isEnterprise: boolean;
  isFree: boolean;
  price: number | null;
  billingInterval: BillingInterval;
  priceMonthly: number | null;
  highlight?: string;
}) {
  return (
    <div className="mb-4">
      {isEnterprise ? (
        <div>
          <span className="tabular-nums text-3xl font-semibold text-foreground">
            Custom
          </span>
          <p className="mt-1 text-xs text-muted-foreground">{highlight}</p>
        </div>
      ) : isFree ? (
        <div>
          <span className="tabular-nums text-3xl font-semibold text-foreground">
            $0
          </span>
          <span className="ml-1 text-sm text-muted-foreground">/mo</span>
        </div>
      ) : (
        <div>
          <span className="tabular-nums text-3xl font-semibold text-foreground">
            ${price}
          </span>
          <span className="ml-1 text-sm text-muted-foreground">/mo</span>
          {billingInterval === "annual" && priceMonthly && (
            <p className="mt-1 text-xs text-success-strong">
              Save ${Math.round((priceMonthly - (price ?? 0)) * 12)}/year
            </p>
          )}
        </div>
      )}
    </div>
  );
}
